/**
 * Safe dock name matching for upstream API results.
 * 
 * Uses normalised exact match instead of .includes() to avoid
 * false positives (e.g. "Halhjem" matching "Halhjemtunnelen").
 */

/** Normalise a dock name for comparison: lowercase, trim, collapse whitespace */
function normalise(name: string): string {
  return name.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Find the best matching stop from an API response.
 * 
 * Priority:
 * 1. Exact normalised name match
 * 2. Stop ID match (if both have id)
 * 3. Falls back to first stop (caller's previous behaviour)
 */
export function findMatchingStop<T extends { name: string; id?: string }>(
  stops: T[],
  targetName: string,
  targetId?: string,
): T | undefined {
  if (stops.length === 0) return undefined;

  const normTarget = normalise(targetName);

  // 1. Exact normalised match
  const exactMatch = stops.find(s => normalise(s.name) === normTarget);
  if (exactMatch) return exactMatch;

  // 2. ID match (if available)
  if (targetId) {
    const idMatch = stops.find(s => s.id === targetId);
    if (idMatch) return idMatch;
  }

  // 3. Fallback to first stop
  return stops[0];
}
